import { api } from './api'

export interface QueryParams {
  workspaceId: string
  accountId?: string
  dateRange?: {
    start: string
    end: string
  }
  search?: string
  filters?: Record<string, any>
  sort?: {
    field: string
    direction: 'ASC' | 'DESC'
  }
  limit?: number
  offset?: number
  lastEvaluatedKey?: any
}

export interface QueryResult<T = any> {
  success: boolean
  data: {
    items: T[]
    pagination: {
      total: number
      limit: number
      offset: number
      hasMore: boolean
    }
    lastEvaluatedKey?: any
    count?: number
    summary?: any
  }
}

// Shopify Data Types
// Customer Intelligence Types
export interface CustomerIntelligence {
  lifecycleStage?: 'prospect' | 'first_purchase' | 'repeat' | 'loyal' | 'vip' | 'at_risk' | 'churned' | 'win_back'
  valueSegment?: 'low' | 'medium' | 'high' | 'vip'
  segments?: string[]
  synthesizedDescription?: string
  communicationPreference?: {
    preferredChannel?: string
    responseStyle?: string
    engagementLevel?: string
    bestTimeToReach?: string[]
    dislikes?: string[]
  }
  openItems?: string[]
  confidenceScore?: number
  behavioralInsights?: {
    shoppingPsychology: string
    decisionDrivers: string[]
    decisionBlockers: string[]
    engagementApproach?: string
    predictedNeeds?: string[]
    customerArchetype?: string
  }
}

export interface OrderIntelligence {
  orderCountAtUpdate?: number
  purchaseFrequencyDays?: number
  preferredCategories?: string[]
  preferredSizes?: Record<string, string>
  priceRange?: { min: number; max: number; avg: number }
  reorderCandidates?: Array<{ title: string; dueInDays: number }>
  daysSinceLastPurchase?: number
  aovTrend?: 'declining' | 'stable' | 'growing'
}

export interface ShopifyCustomer {
  customerId: string
  email: string
  firstName: string
  lastName: string
  phone?: string
  createdAt: string
  updatedAt: string
  totalSpent: number
  ordersCount: number
  tags?: string[]
  acceptsMarketing: boolean
  verifiedEmail: boolean
  currency: string
  // Contact capture fields
  captureSource?: 'shopify' | 'pre_purchase' | 'cart_abandonment' | 'import'
  channelPreference?: 'email' | 'sms' | 'both'
  consentTimestamp?: string
  unsubscribed: boolean
  lastEngagementSessionId?: string
  // Visitor tracking
  marvaVisitorId?: string
  // Merge audit fields
  mergedFromPk?: string
  mergedAt?: string
  // Intelligence data
  customerIntelligence?: CustomerIntelligence
  orderIntelligence?: OrderIntelligence
}

export interface ShopifyOrder {
  order_id: string
  order_number: string
  customer_id?: string
  // Visitor attribution
  marvaVisitorId?: string // Links order to pre-purchase visitor behavior
  cartToken?: string // Cart token for cart session lookup
  checkoutId?: string // Shopify checkout ID
  email: string
  created_at: string
  updated_at: string
  total_price: number
  subtotal_price: number
  total_tax: number
  currency: string
  financial_status: string
  fulfillment_status: string
  line_items_count: number
  tags?: string
  customer_first_name?: string
  customer_last_name?: string
}

export interface ProductExpertCard {
  confidenceScore?: number
  synthesized?: {
    elevatorPitch?: string
    keySellingPoints?: string[]
    idealFor?: string[]
    notIdealFor?: string[]
  }
  extracted?: {
    features?: string[]
    materials?: string[]
    useCases?: string[]
  }
  webResearch?: {
    ratings?: {
      averageRating?: number
      totalReviews?: number
      sentiment?: string
      topPraise?: string[]
      topConcerns?: string[]
    }
    competitorContext?: {
      pricePosition?: string
      alternatives?: Array<{ name: string; price?: string; differentiator?: string }>
    }
    materialAnalysis?: {
      breakdown?: string
      safetyRating?: string
      sustainabilityNotes?: string
    }
    expertReviews?: {
      awards?: string[]
      editorialQuotes?: Array<{ source: string; quote: string }>
    }
  }
  version?: number
  updatedAt?: string
}

export interface ShopifyProduct {
  productId?: string
  product_id?: string
  title: string
  handle: string
  description?: string
  vendor: string
  productType?: string
  product_type?: string
  created_at?: string
  updated_at?: string
  createdAt?: string
  updatedAt?: string
  status: string
  tags?: string | string[]
  variants: any[]
  images: any[]
  variantCount?: number
  variant_count?: number
  imageCount?: number
  image_count?: number
  collections?: any[]
  totalInventory?: number
  presentmentPrices?: Record<string, any>
  metafields?: Record<string, any>
  expertCard?: ProductExpertCard
}

// Behavioral Data Types
export interface TrackingEvent {
  id: string
  timestamp: number
  type: string
  sessionId: string
  // Visitor identity fields
  visitorId?: string // Persistent visitor identifier (extracted from properties.customerId)
  userId?: string
  deviceId: string
  properties: Record<string, any>
  url?: string
  referrer?: string
  userAgent?: string
  source: string
}

export interface TrackingSession {
  sessionId: string
  accountId: string
  workspaceId: string
  // Visitor identity fields
  visitorId?: string // Persistent visitor identifier
  deviceId?: string // Device identifier
  userId?: string // Shopify customer ID if known
  startTime: string
  lastUpdate: string
  metrics: Record<string, any>
  duration?: number
  pageViews: number
  events: number
  entryPage?: string
  exitPage?: string
  bounced: boolean
  converted: boolean
}

export interface CartSession {
  cartId: string
  // Visitor identity fields
  visitorId?: string // Persistent visitor identifier
  customerId?: string
  customerEmail?: string
  customerName?: string
  status: string
  createdAt: number
  updatedAt: number
  abandonedAt?: number
  recoveredAt?: number
  convertedAt?: number
  cartValue: number
  currency: string
  itemCount: number
  items: any[]
  recoveryAttempts: number
  lastRecoveryAt?: number
}

export interface VisitorData {
  date: string
  uniqueVisitors: number
  uniqueSessions: number
  totalEvents: number
  pageViews: number
  averageEventsPerVisitor: number
}

class DataService {
  // Shopify Data Queries (migrated from Snowflake to DynamoDB - Platform Security Phase)
  async queryShopifyCustomers(params: QueryParams): Promise<QueryResult<ShopifyCustomer>> {
    const response = await api.get<QueryResult<ShopifyCustomer>>('/data/dynamodb/customers', { params })
    if (!response.success) {
      throw new Error(response.error || 'Failed to fetch customers')
    }
    return response.data!
  }

  async getCustomerById(params: { accountId: string; workspaceId: string; customerId: string }): Promise<ShopifyCustomer | null> {
    const response = await api.get<QueryResult<ShopifyCustomer>>('/data/dynamodb/customers', {
      params: {
        accountId: params.accountId,
        workspaceId: params.workspaceId,
        filters: { customerId: params.customerId },
        limit: 1
      }
    })
    if (!response.success) {
      throw new Error(response.error || 'Failed to fetch customer')
    }
    const result = response.data as any
    const items = result?.items || result?.data?.items || []
    return items.length > 0 ? items[0] : null
  }

  async queryShopifyOrders(params: QueryParams): Promise<QueryResult<ShopifyOrder>> {
    const response = await api.get<QueryResult<ShopifyOrder>>('/data/dynamodb/orders', { params })
    if (!response.success) {
      throw new Error(response.error || 'Failed to fetch orders')
    }
    return response.data!
  }

  async queryShopifyProducts(params: QueryParams): Promise<QueryResult<ShopifyProduct>> {
    const response = await api.get<QueryResult<ShopifyProduct>>('/data/dynamodb/products', { params })
    if (!response.success) {
      throw new Error(response.error || 'Failed to fetch products')
    }
    return response.data!
  }

  async getProductById(params: { accountId: string; workspaceId: string; productId: string }): Promise<ShopifyProduct | null> {
    const response = await api.get<QueryResult<ShopifyProduct>>('/data/dynamodb/products', {
      params: {
        accountId: params.accountId,
        workspaceId: params.workspaceId,
        filters: { productId: params.productId },
        limit: 1
      }
    })
    if (!response.success) {
      throw new Error(response.error || 'Failed to fetch product')
    }
    const result = response.data as any
    const items = result?.items || result?.data?.items || []
    return items.length > 0 ? items[0] : null
  }

  // Behavioral Data Queries (from DynamoDB)
  async queryTrackingEvents(params: QueryParams): Promise<QueryResult<TrackingEvent>> {
    const response = await api.get<QueryResult<TrackingEvent>>('/data/dynamodb/events', { params })
    if (!response.success) {
      throw new Error(response.error || 'Failed to fetch events')
    }
    return response.data!
  }

  async queryTrackingSessions(params: QueryParams): Promise<QueryResult<TrackingSession>> {
    const response = await api.get<QueryResult<TrackingSession>>('/data/dynamodb/sessions', { params })
    if (!response.success) {
      throw new Error(response.error || 'Failed to fetch sessions')
    }
    return response.data!
  }

  async queryCartSessions(params: QueryParams): Promise<QueryResult<CartSession>> {
    const response = await api.get<QueryResult<CartSession>>('/data/dynamodb/carts', { params })
    if (!response.success) {
      throw new Error(response.error || 'Failed to fetch cart sessions')
    }
    return response.data!
  }

  async queryVisitors(params: QueryParams & { groupBy?: 'day' | 'week' | 'month' }): Promise<QueryResult<VisitorData>> {
    const response = await api.get<QueryResult<VisitorData>>('/data/dynamodb/visitors', { params })
    if (!response.success) {
      throw new Error(response.error || 'Failed to fetch visitor data')
    }
    return response.data!
  }

  // Export functionality (all data now from DynamoDB - Platform Security Phase)
  async exportData(
    entity: string,
    format: 'csv' | 'json',
    _params: QueryParams
  ): Promise<Blob> {
    // For now, return mock CSV data since we don't have blob support in the API client
    const csvData = `Entity,Source,Format\n${entity},dynamodb,${format}`
    return new Blob([csvData], { type: 'text/csv' })
  }

  // Utility method to build query params
  buildQueryParams(baseParams: {
    accountId: string
    workspaceId: string
  }, options: {
    dateRange?: { start: Date; end: Date }
    search?: string
    filters?: Record<string, any>
    sort?: { field: string; direction: 'ASC' | 'DESC' }
    pagination?: { limit: number; offset: number }
    lastEvaluatedKey?: any
  } = {}): QueryParams {
    const params: QueryParams = {
      ...baseParams
    }

    if (options.dateRange) {
      params.dateRange = {
        start: options.dateRange.start.toISOString(),
        end: options.dateRange.end.toISOString()
      }
    }

    if (options.search) {
      params.search = options.search
    }

    if (options.filters) {
      params.filters = options.filters
    }

    if (options.sort) {
      params.sort = options.sort
    }

    if (options.pagination) {
      params.limit = options.pagination.limit
      params.offset = options.pagination.offset
    }

    if (options.lastEvaluatedKey) {
      params.lastEvaluatedKey = options.lastEvaluatedKey
    }

    return params
  }
}

export const dataService = new DataService()
